import React, {
  PropsWithChildren,
  createContext,
  useContext,
  useMemo,
} from "react";

import { useLocalStorage } from "usehooks-ts";

type ContextValues = {
  currentShowroomId?: string;
  compareToShowroomId?: string;
};

type ContextActions = {
  setCurrentShowroomId: (showroom: string | undefined) => void;
  setCompareToShowroomId: (showroom: string | undefined) => void;
};

type DashboardFiltersContextType = ContextValues & ContextActions;

export const DashboardFiltersContext =
  createContext<DashboardFiltersContextType>({
    currentShowroomId: undefined,
    compareToShowroomId: undefined,
    setCurrentShowroomId: () => {},
    setCompareToShowroomId: () => {},
  });

export function useDashboardFiltersContext() {
  return useContext(DashboardFiltersContext);
}

export function DashboardFiltersContextProvider({
  name,
  children,
}: PropsWithChildren<{ name: string }>) {
  // those are the values selected
  const [filters, setFilters] = useLocalStorage<
    Pick<ContextValues, "currentShowroomId" | "compareToShowroomId">
  >(name, {
    currentShowroomId: undefined,
    compareToShowroomId: undefined,
  });

  const memoizedContext = useMemo(
    () => ({
      ...filters,
      setCurrentShowroomId: (showroom: string | undefined) => {
        setFilters({
          ...filters,
          currentShowroomId: showroom,
        });
      },
      setCompareToShowroomId: (showroom: string | undefined) => {
        setFilters({
          ...filters,
          compareToShowroomId: showroom,
        });
      },
    }),
    [filters, setFilters],
  );

  return (
    <DashboardFiltersContext.Provider value={memoizedContext}>
      {children}
    </DashboardFiltersContext.Provider>
  );
}
